/**
 * Resolve a location/locality string to its city (for "this location belongs to X city" modal).
 * Uses API (suggestions without city) then fallback to static map.
 */
import { fetchData } from "../api";

const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

const CITY_SLUG_TO_NAME = {
  bangalore: "Bangalore",
  bengaluru: "Bangalore",
  hyderabad: "Hyderabad",
  chennai: "Chennai",
  mumbai: "Mumbai",
  pune: "Pune",
  delhi: "Delhi",
  "new-delhi": "New Delhi",
  noida: "Noida",
  gurgaon: "Gurgaon",
  gurugram: "Gurugram",
  kolkata: "Kolkata",
  ahmedabad: "Ahmedabad",
  coimbatore: "Coimbatore",
  mysore: "Mysore",
  kochi: "Kochi",
  thrissur: "Thrissur",
};

/** Known localities -> { citySlug, cityName }. Keys are lowercase. */
const LOCATION_TO_CITY = {
  // Bangalore
  koramangala: { citySlug: "bangalore", cityName: "Bangalore" },
  whitefield: { citySlug: "bangalore", cityName: "Bangalore" },
  indiranagar: { citySlug: "bangalore", cityName: "Bangalore" },
  jayanagar: { citySlug: "bangalore", cityName: "Bangalore" },
  hsr: { citySlug: "bangalore", cityName: "Bangalore" },
  "hsr layout": { citySlug: "bangalore", cityName: "Bangalore" },
  marathahalli: { citySlug: "bangalore", cityName: "Bangalore" },
  bellandur: { citySlug: "bangalore", cityName: "Bangalore" },
  sarjapur: { citySlug: "bangalore", cityName: "Bangalore" },
  electronic: { citySlug: "bangalore", cityName: "Bangalore" },
  "electronic city": { citySlug: "bangalore", cityName: "Bangalore" },
  "mg road": { citySlug: "bangalore", cityName: "Bangalore" },
  "mg road bangalore": { citySlug: "bangalore", cityName: "Bangalore" },
  btm: { citySlug: "bangalore", cityName: "Bangalore" },
  "btm layout": { citySlug: "bangalore", cityName: "Bangalore" },
  varthur: { citySlug: "bangalore", cityName: "Bangalore" },
  "varthur road": { citySlug: "bangalore", cityName: "Bangalore" },
  kadubeesanahalli: { citySlug: "bangalore", cityName: "Bangalore" },
  brookefield: { citySlug: "bangalore", cityName: "Bangalore" },
  "brookefield bangalore": { citySlug: "bangalore", cityName: "Bangalore" },
  "sarjapur road": { citySlug: "bangalore", cityName: "Bangalore" },
  ecospace: { citySlug: "bangalore", cityName: "Bangalore" },
  embassy: { citySlug: "bangalore", cityName: "Bangalore" },
  manyata: { citySlug: "bangalore", cityName: "Bangalore" },
  hebbal: { citySlug: "bangalore", cityName: "Bangalore" },
  yemalur: { citySlug: "bangalore", cityName: "Bangalore" },
  // Hyderabad
  gachibowli: { citySlug: "hyderabad", cityName: "Hyderabad" },
  madhapur: { citySlug: "hyderabad", cityName: "Hyderabad" },
  madapur: { citySlug: "hyderabad", cityName: "Hyderabad" },
  kondapur: { citySlug: "hyderabad", cityName: "Hyderabad" },
  kukatpally: { citySlug: "hyderabad", cityName: "Hyderabad" },
  "hi-tech city": { citySlug: "hyderabad", cityName: "Hyderabad" },
  hitech: { citySlug: "hyderabad", cityName: "Hyderabad" },
  "jubilee hills": { citySlug: "hyderabad", cityName: "Hyderabad" },
  "banjara hills": { citySlug: "hyderabad", cityName: "Hyderabad" },
  tellapur: { citySlug: "hyderabad", cityName: "Hyderabad" },
  kokapet: { citySlug: "hyderabad", cityName: "Hyderabad" },
  kollur: { citySlug: "hyderabad", cityName: "Hyderabad" },
  narsingi: { citySlug: "hyderabad", cityName: "Hyderabad" },
  shamshabad: { citySlug: "hyderabad", cityName: "Hyderabad" },
  // Chennai
  "t nagar": { citySlug: "chennai", cityName: "Chennai" },
  tnagar: { citySlug: "chennai", cityName: "Chennai" },
  "anna nagar": { citySlug: "chennai", cityName: "Chennai" },
  annanagar: { citySlug: "chennai", cityName: "Chennai" },
  omr: { citySlug: "chennai", cityName: "Chennai" },
  "old mahabalipuram road": { citySlug: "chennai", cityName: "Chennai" },
  velachery: { citySlug: "chennai", cityName: "Chennai" },
  porur: { citySlug: "chennai", cityName: "Chennai" },
  // Mumbai
  andheri: { citySlug: "mumbai", cityName: "Mumbai" },
  bandra: { citySlug: "mumbai", cityName: "Mumbai" },
  powai: { citySlug: "mumbai", cityName: "Mumbai" },
  goregaon: { citySlug: "mumbai", cityName: "Mumbai" },
  thane: { citySlug: "mumbai", cityName: "Mumbai" },
  "navi mumbai": { citySlug: "mumbai", cityName: "Mumbai" },
  // Pune
  hinjewadi: { citySlug: "pune", cityName: "Pune" },
  "viman nagar": { citySlug: "pune", cityName: "Pune" },
  "koregaon park": { citySlug: "pune", cityName: "Pune" },
  kharadi: { citySlug: "pune", cityName: "Pune" },
};

/**
 * Synchronous lookup: get city for a location slug using static map only.
 * Use for breadcrumbs when we have location but no city in URL/overrides.
 * @param {string} locationSlug - URL slug e.g. "kollur", "jubilee-hills"
 * @returns {{ citySlug: string, cityName: string } | null}
 */
export function getCityForLocationSync(locationSlug) {
  if (!locationSlug || typeof locationSlug !== "string") return null;
  const key = locationSlug.trim().toLowerCase();
  const keySpaces = key.replace(/-/g, " ");
  return LOCATION_TO_CITY[key] || LOCATION_TO_CITY[keySpaces] || null;
}

/**
 * Get city slug/name from suggestion item (API may return city_slug, city, city_name).
 */
function getCityFromSuggestion(item) {
  if (!item) return null;
  const raw = item.city_slug || item.city_slug_id || item.city;
  const slug = (raw != null && typeof raw === "string") ? raw.trim().toLowerCase().replace(/\s+/g, "-") : "";
  const name = (item.city_name || (typeof item.city === "string" ? item.city : "") || "").toString().trim();
  if (!slug) return null;
  const cityName = name || CITY_SLUG_TO_NAME[slug] || slug.replace(/-/g, " ");
  return { citySlug: slug, cityName };
}

/**
 * Resolve location text to which city it belongs.
 * @param {string} locationText - Locality/location name user searched
 * @param {string} typeParam - e.g. apartment_buy, villa_buy, plot_buy
 * @param {string} currentCitySlug - Current selected city (if result is same, returns null)
 * @returns {Promise<{ citySlug: string, cityName: string } | null>} - null if same city or could not resolve
 */
export async function getCityForLocation(locationText, typeParam, currentCitySlug) {
  const trimmed = (locationText || "").trim();
  if (!trimmed) return null;

  const currentNorm = (normalize(currentCitySlug) || "hyderabad").replace(/\s+/g, "-");
  const locationNorm = normalize(trimmed);
  const primaryPart = locationNorm.split(",")[0].trim() || locationNorm;

  // 1) Static map: try exact key, then primary part (e.g. "Koramangala, Bangalore" -> "koramangala"), then partial match
  const exact = LOCATION_TO_CITY[locationNorm] || LOCATION_TO_CITY[primaryPart];
  if (exact) {
    if (exact.citySlug === currentNorm) return null;
    return exact;
  }
  for (const [key, value] of Object.entries(LOCATION_TO_CITY)) {
    if (key.length >= 2 && (locationNorm.includes(key) || primaryPart.includes(key) || locationNorm === key)) {
      if (value.citySlug === currentNorm) return null;
      return value;
    }
  }

  // 2) API: fetch suggestions without city to get suggestions from all cities; pick first with city_slug
  try {
    const res = await fetchData(
      `search/suggestions?type=${encodeURIComponent(typeParam)}&query=${encodeURIComponent(trimmed)}`
    );
    const items = Array.isArray(res?.data) ? res.data : [];
    const locationType = (t) => {
      const s = String(t || "").toLowerCase();
      return s.includes("location") || s.includes("locality") || s.includes("area") || s.includes("city");
    };
    for (const item of items) {
      const label = (item?.name || item?.title || item?.label || "").toString().trim().toLowerCase();
      if (!label || !locationType(item?.type)) continue;
      const match = label === locationNorm || label.includes(locationNorm) || locationNorm.includes(label);
      if (!match) continue;
      const city = getCityFromSuggestion(item);
      if (city && city.citySlug !== currentNorm) return city;
      if (city && city.citySlug === currentNorm) return null;
    }
  } catch (_) {
    // ignore
  }

  return null;
}
